import { inject, Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';
import { TranslateService } from '../i18n/translate.service';

/**
 * Servicio global para detectar pérdida y recuperación de conexión.
 * - Muestra toast de advertencia cuando no hay internet (por evento offline o error HTTP).
 * - Muestra toast de éxito cuando se recupera la conexión (evento online).
 */
@Injectable({
  providedIn: 'root',
})
export class NetworkStatusService {
  private readonly messageService = inject(MessageService);
  private readonly translateService = inject(TranslateService);

  private offlineToastShown = false;

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('offline', () => this.showOfflineToast());
      window.addEventListener('online', () => this.showOnlineToast());
    }
  }

  /**
   * Llamado por el interceptor cuando una petición falla por red (ej. ERR_INTERNET_DISCONNECTED).
   * Evita mostrar el toast en cada request fallido usando un flag.
   */
  showOfflineToast(): void {
    if (this.offlineToastShown) return;
    this.offlineToastShown = true;
    this.messageService.add({
      severity: 'warn',
      summary: this.translateService.translate('common.network.offline.summary'),
      detail: this.translateService.translate('common.network.offline.detail'),
      life: 6000,
    });
  }

  private showOnlineToast(): void {
    if (!this.offlineToastShown) return;
    this.offlineToastShown = false;
    this.messageService.add({
      severity: 'success',
      summary: this.translateService.translate('common.network.online.summary'),
      detail: this.translateService.translate('common.network.online.detail'),
      life: 4000,
    });
  }
}
